import { useState, useEffect } from 'react'
import { ticketsAPI, customersAPI } from '../../services/api'

import { logger } from '@/utils/logger';
export default function SupportManagement() {
  const [tickets, setTickets] = useState([])
  const [customers, setCustomers] = useState([])
  const [selectedTicket, setSelectedTicket] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [filterStatus, setFilterStatus] = useState(null)
  const [replyMessage, setReplyMessage] = useState('')
  const [isInternal, setIsInternal] = useState(false)
  const [replyLoading, setReplyLoading] = useState(false)

  useEffect(() => {
    loadData()
  }, [filterStatus])

  const loadData = async () => {
    setLoading(true)
    try {
      const [ticketsResponse, customersResponse] = await Promise.all([
        ticketsAPI.getAllAdmin(filterStatus),
        customersAPI.getAll()
      ])
      setTickets(ticketsResponse.data)
      setCustomers(customersResponse.data)
    } catch (err) {
      setError('Fehler beim Laden der Daten')
      logger.error('Error loading data:', err)
    } finally {
      setLoading(false)
    }
  }

  const loadTicketDetails = async (ticketId) => {
    try {
      const response = await ticketsAPI.getByIdAdmin(ticketId)
      setSelectedTicket(response.data)
    } catch (err) {
      setError('Fehler beim Laden des Tickets')
      logger.error('Error loading ticket:', err)
    }
  }

  const handleSelectTicket = (ticket) => {
    setSelectedTicket(ticket)
    loadTicketDetails(ticket.id)
    setReplyMessage('')
    setIsInternal(false)
  }

  const handleUpdateTicket = async (ticketId, updates) => {
    try {
      await ticketsAPI.updateAdmin(ticketId, updates)
      setSuccess('Ticket erfolgreich aktualisiert')
      await loadData()
      if (selectedTicket?.id === ticketId) {
        await loadTicketDetails(ticketId)
      }
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      setError('Fehler beim Aktualisieren des Tickets')
      logger.error('Error updating ticket:', err)
      setTimeout(() => setError(''), 3000)
    }
  }

  const handleAddReply = async (e) => {
    e.preventDefault()
    if (!replyMessage.trim()) return

    setReplyLoading(true)
    setError('')
    setSuccess('')

    try {
      await ticketsAPI.addReplyAdmin(selectedTicket.id, replyMessage, isInternal)
      setSuccess(isInternal ? 'Interne Notiz hinzugefügt' : 'Antwort gesendet')
      setReplyMessage('')
      setIsInternal(false)
      await loadTicketDetails(selectedTicket.id)
      await loadData()
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      setError('Fehler beim Senden der Antwort')
      logger.error('Error adding reply:', err)
      setTimeout(() => setError(''), 3000)
    } finally {
      setReplyLoading(false)
    }
  }

  const handleDeleteTicket = async (ticketId) => {
    if (!confirm('Möchten Sie dieses Ticket wirklich löschen?')) return

    try {
      await ticketsAPI.deleteAdmin(ticketId)
      setSuccess('Ticket erfolgreich gelöscht')
      setSelectedTicket(null)
      await loadData()
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      setError('Fehler beim Löschen des Tickets')
      logger.error('Error deleting ticket:', err)
      setTimeout(() => setError(''), 3000)
    }
  }

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId)
    return customer ? (customer.company_name || customer.username) : 'Unbekannt'
  }

  const getCustomerEmail = (customerId) => {
    const customer = customers.find(c => c.id === customerId)
    return customer?.email || ''
  }

  const statusTag = {
    open: { className: 'wc-tag-outline', label: 'Offen' },
    in_progress: { className: 'wc-tag-accent', label: 'In Bearbeitung' },
    waiting_customer: { className: 'wc-tag-accent-2', label: 'Wartet auf Kunde' },
    resolved: { className: 'wc-tag-accent', label: 'Gelöst' },
    closed: { className: 'wc-tag-neutral', label: 'Geschlossen' },
  }

  const priorityTag = {
    low: { className: 'wc-tag-neutral', label: 'Niedrig' },
    medium: { className: 'wc-tag-outline', label: 'Mittel' },
    high: { className: 'wc-tag-accent-2', label: 'Hoch' },
    urgent: { className: 'wc-tag-accent', label: 'Dringend' },
  }

  const getStatusBadge = (status) => {
    const c = statusTag[status] || statusTag.open
    return <span className={`wc-tag ${c.className}`}>{c.label}</span>
  }

  const getPriorityBadge = (priority) => {
    const c = priorityTag[priority] || priorityTag.medium
    return <span className={`wc-tag ${c.className}`}>{c.label}</span>
  }

  const getCategoryLabel = (category) => {
    const labels = {
      general: 'Allgemein',
      technical: 'Technisch',
      billing: 'Abrechnung',
      account: 'Account',
      feature: 'Feature-Anfrage',
      cancellation: 'Kündigung'
    }
    return labels[category] || category
  }

  const formatDateTime = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleString('de-DE')
  }

  const statusFilters = [
    { value: null, label: 'Alle' },
    { value: 'open', label: 'Offen' },
    { value: 'in_progress', label: 'In Bearbeitung' },
    { value: 'resolved', label: 'Gelöst' },
  ]

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
        <div className="wc-spinner" />
      </div>
    )
  }

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 24, margin: '0 0 4px' }}>Support &amp; Tickets</h1>
        <p className="wc-muted" style={{ fontSize: 14, margin: 0 }}>Verwaltung aller Kunden-Support-Anfragen</p>
      </div>

      {error && <div className="wc-alert wc-alert-error" style={{ marginBottom: 16 }}>{error}</div>}
      {success && <div className="wc-alert wc-alert-success" style={{ marginBottom: 16 }}>{success}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)', gap: 20, alignItems: 'start' }}>
        {/* Tickets List */}
        <div className="wc-card wc-elev-sm" style={{ padding: 0, display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 220px)' }}>
          <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid var(--wc-divider)' }}>
            <h3 className="wc-card-title" style={{ margin: '0 0 12px' }}>Tickets ({tickets.length})</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {statusFilters.map((f) => (
                <button
                  key={f.label}
                  onClick={() => setFilterStatus(f.value)}
                  className={`wc-btn ${filterStatus === f.value ? 'wc-btn-primary' : 'wc-btn-secondary'}`}
                  style={{ padding: '5px 12px', fontSize: 12 }}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
            {tickets.length === 0 ? (
              <p className="wc-muted" style={{ textAlign: 'center', padding: '32px 0' }}>Keine Tickets gefunden</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {tickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    onClick={() => handleSelectTicket(ticket)}
                    style={{
                      padding: 12,
                      borderRadius: 'var(--wc-radius-md)',
                      border: selectedTicket?.id === ticket.id ? '1px solid var(--wc-accent)' : '1px solid var(--wc-divider)',
                      background: selectedTicket?.id === ticket.id ? 'color-mix(in srgb, var(--wc-accent) 10%, transparent)' : 'transparent',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span className="wc-mono wc-muted" style={{ fontSize: 12 }}>#{ticket.id}</span>
                      {getStatusBadge(ticket.status)}
                    </div>
                    <div style={{ fontSize: 14, marginBottom: 4 }}>{ticket.subject}</div>
                    <div className="wc-muted" style={{ fontSize: 12, marginBottom: 6 }}>{getCustomerName(ticket.customer_id)}</div>
                    <div className="wc-muted" style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                      <span>{getCategoryLabel(ticket.category)}</span>
                      <span>{formatDateTime(ticket.created_at).split(',')[0]}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Ticket Details */}
        <div className="wc-card wc-elev-sm" style={{ padding: 0, display: 'flex', flexDirection: 'column', minHeight: 420, maxHeight: 'calc(100vh - 220px)' }}>
          {!selectedTicket ? (
            <div className="wc-muted" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>
              Wählen Sie ein Ticket aus, um Details anzuzeigen
            </div>
          ) : (
            <>
              {/* Ticket Header */}
              <div style={{ padding: 20, borderBottom: '1px solid var(--wc-divider)' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
                  <div style={{ minWidth: 0 }}>
                    <h3 className="wc-card-title" style={{ margin: '0 0 6px' }}>{selectedTicket.subject}</h3>
                    <div className="wc-muted" style={{ display: 'flex', gap: 8, fontSize: 13, flexWrap: 'wrap' }}>
                      <span>Ticket #{selectedTicket.id}</span>
                      <span>·</span>
                      <span>{getCustomerName(selectedTicket.customer_id)}</span>
                      <span>·</span>
                      <span>{getCustomerEmail(selectedTicket.customer_id)}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteTicket(selectedTicket.id)}
                    className="wc-btn wc-btn-ghost"
                    style={{ color: '#fca5a5', flexShrink: 0 }}
                  >
                    Löschen
                  </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
                  <div className="wc-field">
                    <label>Status</label>
                    <select
                      value={selectedTicket.status}
                      onChange={(e) => handleUpdateTicket(selectedTicket.id, { status: e.target.value })}
                      className="wc-input"
                      style={{ fontSize: 13 }}
                    >
                      <option value="open">Offen</option>
                      <option value="in_progress">In Bearbeitung</option>
                      <option value="waiting_customer">Wartet auf Kunde</option>
                      <option value="resolved">Gelöst</option>
                      <option value="closed">Geschlossen</option>
                    </select>
                  </div>
                  <div className="wc-field">
                    <label>Priorität</label>
                    <select
                      value={selectedTicket.priority}
                      onChange={(e) => handleUpdateTicket(selectedTicket.id, { priority: e.target.value })}
                      className="wc-input"
                      style={{ fontSize: 13 }}
                    >
                      <option value="low">Niedrig</option>
                      <option value="medium">Mittel</option>
                      <option value="high">Hoch</option>
                      <option value="urgent">Dringend</option>
                    </select>
                  </div>
                  <div>
                    <div className="wc-muted" style={{ fontSize: 12, marginBottom: 5 }}>Kategorie</div>
                    <div style={{ fontSize: 13, padding: '6px 0' }}>{getCategoryLabel(selectedTicket.category)}</div>
                  </div>
                  <div>
                    <div className="wc-muted" style={{ fontSize: 12, marginBottom: 5 }}>Erstellt</div>
                    <div style={{ fontSize: 13, padding: '6px 0' }}>{formatDateTime(selectedTicket.created_at)}</div>
                  </div>
                </div>
              </div>

              {/* Replies */}
              <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
                {selectedTicket.replies && selectedTicket.replies.length === 0 ? (
                  <p className="wc-muted" style={{ textAlign: 'center', padding: '32px 0' }}>Keine Nachrichten</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {selectedTicket.replies?.map((reply) => (
                      <div
                        key={reply.id}
                        style={{
                          padding: 14,
                          borderRadius: 'var(--wc-radius-md)',
                          border: '1px solid var(--wc-divider)',
                          background: reply.is_internal
                            ? 'color-mix(in srgb, var(--wc-accent-2) 12%, transparent)'
                            : reply.user_id
                            ? 'color-mix(in srgb, var(--wc-accent) 10%, transparent)'
                            : 'color-mix(in srgb, var(--wc-text) 5%, transparent)',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 13, fontWeight: 600 }}>
                              {reply.user_id ? 'Admin' : getCustomerName(reply.customer_id)}
                            </span>
                            {reply.is_internal && <span className="wc-tag wc-tag-accent-2">Intern</span>}
                          </div>
                          <span className="wc-muted" style={{ fontSize: 12 }}>{formatDateTime(reply.created_at)}</span>
                        </div>
                        <p style={{ margin: 0, fontSize: 14, whiteSpace: 'pre-wrap' }}>{reply.message}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Reply Form */}
              <div style={{ padding: 20, borderTop: '1px solid var(--wc-divider)' }}>
                <form onSubmit={handleAddReply}>
                  <textarea
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    placeholder="Antwort schreiben..."
                    rows={3}
                    className="wc-input"
                    required
                  />
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
                    <label className="wc-check">
                      <input
                        type="checkbox"
                        checked={isInternal}
                        onChange={(e) => setIsInternal(e.target.checked)}
                      />
                      Interne Notiz (nicht für Kunde sichtbar)
                    </label>
                    <button
                      type="submit"
                      disabled={replyLoading || !replyMessage.trim()}
                      className="wc-btn wc-btn-primary"
                    >
                      {replyLoading ? 'Wird gesendet...' : (isInternal ? 'Notiz hinzufügen' : 'Antworten')}
                    </button>
                  </div>
                </form>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
